import React, { useState, useRef, useEffect } from "react";
import {
  Drawer,
  Input,
  Button,
  Avatar,
  Empty,
  Tag,
  Tooltip,
  Spin,
  Popconfirm,
  Divider,
} from "antd";
import {
  SendOutlined,
  UserOutlined,
  GlobalOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  SwapOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const { TextArea } = Input;

const QUILL_MODULES = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["clean"],
  ],
};

const QUILL_FORMATS = ["bold", "italic", "underline", "list", "bullet"];

export default function ConversationDrawer({
  card,
  currentUser,
  messages,
  messagesLoading,
  sendingMessage,
  onClose,
  onSend,
  onDeleteMessage,
  allColumns,
  onMoveColumn,
  onUpdateDescription, // (cardId, description) => Promise
}) {
  const [text, setText] = useState("");
  const [description, setDescription] = useState("");
  const [editingDesc, setEditingDesc] = useState(false);
  const [savingDesc, setSavingDesc] = useState(false);
  const messagesEndRef = useRef(null);

  // Sync description when card changes
  useEffect(() => {
    if (card) {
      setDescription(card.description || "");
      setEditingDesc(false);
    }
    setText("");
  }, [card?._id]);

  // Scroll messages to bottom
  useEffect(() => {
    if (!messagesLoading) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 80);
    }
  }, [messages?.length, messagesLoading]);

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || sendingMessage) return;
    onSend(trimmed);
    setText("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSaveDescription = async () => {
    setSavingDesc(true);
    try {
      await onUpdateDescription(card._id, description);
      setEditingDesc(false);
    } finally {
      setSavingDesc(false);
    }
  };

  const handleCancelDescription = () => {
    setDescription(card.description || "");
    setEditingDesc(false);
  };

  if (!card) return null;

  const createdBy = card.createdBy?.name || "—";
  const assignedTo = card.assignedTo?.name || "—";
  const otherColumns =
    allColumns?.filter((col) => col.key !== card.column) || [];

  // Strip HTML tags for empty check
  const descriptionIsEmpty =
    !description || description.replace(/<(.|\n)*?>/g, "").trim() === "";

  return (
    <Drawer
      open={!!card}
      onClose={onClose}
      width={820}
      title={null}
      closable={false}
      bodyStyle={{
        padding: 0,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* ── Top Header ─────────────────────────────── */}
      <div className="drawer-header">
        <div className="drawer-header-top">
          <div style={{ flex: 1 }}>
            <h3 className="drawer-job-title">{card.jobName}</h3>
            <div className="drawer-meta-row">
              <span className="drawer-meta-item">
                <GlobalOutlined style={{ marginRight: 4 }} />
                {card.region}
              </span>
              <span className="drawer-meta-divider">·</span>
              <span className="drawer-meta-item">
                <DollarOutlined style={{ marginRight: 4 }} />$
                {Number(card.projectValue).toLocaleString()}
              </span>
              <span className="drawer-meta-divider">·</span>
              <span className="drawer-meta-item">
                <ClockCircleOutlined style={{ marginRight: 4 }} />
                {card.duration}
              </span>
              {card.hourlyRate && (
                <>
                  <span className="drawer-meta-divider">·</span>
                  <span className="drawer-meta-item">
                    <DollarOutlined style={{ marginRight: 4 }} />$
                    {card.hourlyRate}/hr
                  </span>
                </>
              )}
            </div>
          </div>
          <button className="drawer-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* People + Move */}
        <div className="drawer-header-bottom">
          <div className="drawer-tags-row">
            <Tooltip title="Created by">
              <Tag icon={<UserOutlined />} className="drawer-tag-created">
                {createdBy}
              </Tag>
            </Tooltip>
            <Tooltip title="Assigned to">
              <Tag icon={<UserOutlined />} className="drawer-tag-assigned">
                {assignedTo}
              </Tag>
            </Tooltip>
          </div>

          {otherColumns.length > 0 && (
            <div className="drawer-move-row">
              <SwapOutlined
                style={{ color: "rgba(255,255,255,0.6)", marginRight: 6 }}
              />
              <span className="drawer-move-label">Move to:</span>
              {otherColumns.map((col) => (
                <button
                  key={col.key}
                  className="drawer-move-btn"
                  style={{ borderColor: col.color, color: col.color }}
                  onClick={() => onMoveColumn(card._id, card.column, col.key)}
                >
                  {col.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Two Panel Body ─────────────────────────── */}
      <div className="drawer-body">
        {/* ── LEFT: Details + Description ──────────── */}
        <div className="drawer-left">
          <div className="drawer-panel-label">Details</div>

          <div className="drawer-detail-grid">
            <div className="drawer-detail-item">
              <span className="drawer-detail-key">Region</span>
              <span className="drawer-detail-val">{card.region}</span>
            </div>
            <div className="drawer-detail-item">
              <span className="drawer-detail-key">Project Value</span>
              <span className="drawer-detail-val">
                ${Number(card.projectValue).toLocaleString()}
              </span>
            </div>
            <div className="drawer-detail-item">
              <span className="drawer-detail-key">Duration</span>
              <span className="drawer-detail-val">{card.duration}</span>
            </div>
            {card.hourlyRate && (
              <div className="drawer-detail-item">
                <span className="drawer-detail-key">Hourly Rate</span>
                <span className="drawer-detail-val">${card.hourlyRate}/hr</span>
              </div>
            )}
            <div className="drawer-detail-item">
              <span className="drawer-detail-key">Created By</span>
              <span className="drawer-detail-val">{createdBy}</span>
            </div>
            <div className="drawer-detail-item">
              <span className="drawer-detail-key">Assigned To</span>
              <span className="drawer-detail-val">{assignedTo}</span>
            </div>
            <div className="drawer-detail-item">
              <span className="drawer-detail-key">Created At</span>
              <span className="drawer-detail-val">
                {card.createdAt
                  ? new Date(card.createdAt).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                  : "—"}
              </span>
            </div>
          </div>

          <Divider style={{ margin: "16px 0" }} />

          <div className="drawer-desc-header">
            <span className="drawer-panel-label" style={{ marginBottom: 0 }}>
              Description
            </span>
            {!editingDesc ? (
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => setEditingDesc(true)}
                className="drawer-desc-edit-btn"
              >
                Edit
              </Button>
            ) : (
              <div style={{ display: "flex", gap: 6 }}>
                <Button
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={handleCancelDescription}
                  disabled={savingDesc}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={handleSaveDescription}
                  loading={savingDesc}
                  style={{ background: "#3699FF", borderColor: "#3699FF" }}
                >
                  Save
                </Button>
              </div>
            )}
          </div>

          {editingDesc ? (
            <div className="drawer-quill-wrapper">
              <ReactQuill
                theme="snow"
                value={description}
                onChange={setDescription}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Add a description for this requirement..."
              />
            </div>
          ) : (
            <div
              className="drawer-desc-display"
              onClick={() => setEditingDesc(true)}
            >
              {descriptionIsEmpty ? (
                <span className="drawer-desc-placeholder">
                  Click to add a description...
                </span>
              ) : (
                <div
                  className="ql-editor drawer-desc-content"
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              )}
            </div>
          )}
        </div>

        {/* ── RIGHT: Conversation ───────────────────── */}
        <div className="drawer-right">
          <div className="drawer-panel-label">
            Conversation
            {messages?.length > 0 && (
              <span className="drawer-msg-count-label">
                {" "}
                · {messages.length}
              </span>
            )}
          </div>

          {/* Messages */}
          <div className="drawer-messages">
            {messagesLoading ? (
              <div className="drawer-messages-loading">
                <Spin tip="Loading..." />
              </div>
            ) : messages?.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No messages yet"
                style={{ marginTop: 40 }}
              />
            ) : (
              messages.map((msg) => {
                const isMe = msg.author?._id === currentUser?._id;
                const authorName = msg.author?.name || "Unknown";
                const timeStr = msg.createdAt
                  ? new Date(msg.createdAt).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "";

                return (
                  <div
                    key={msg._id}
                    className={`drawer-msg-row ${isMe ? "drawer-msg-row--me" : "drawer-msg-row--other"}`}
                  >
                    {!isMe && (
                      <Avatar
                        size={28}
                        style={{ background: "#13487D", flexShrink: 0 }}
                      >
                        {authorName.charAt(0)}
                      </Avatar>
                    )}
                    <div
                      className={`drawer-msg-bubble ${isMe ? "drawer-msg-bubble--me" : "drawer-msg-bubble--other"}`}
                    >
                      {!isMe && (
                        <span className="drawer-msg-author">{authorName}</span>
                      )}
                      <p className="drawer-msg-text">{msg.text}</p>
                      <div className="drawer-msg-footer">
                        <span className="drawer-msg-time">
                          {msg.isOptimistic ? "Sending…" : timeStr}
                        </span>
                        {isMe && !msg.isOptimistic && (
                          <Popconfirm
                            title="Delete this message?"
                            okText="Delete"
                            cancelText="Cancel"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => onDeleteMessage(msg._id)}
                          >
                            <DeleteOutlined className="drawer-msg-delete" />
                          </Popconfirm>
                        )}
                      </div>
                    </div>
                    {isMe && (
                      <Avatar
                        size={28}
                        style={{ background: "#3699FF", flexShrink: 0 }}
                      >
                        {currentUser?.name?.charAt(0) || "U"}
                      </Avatar>
                    )}
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="drawer-input-area">
            <TextArea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message… (Enter to send)"
              autoSize={{ minRows: 2, maxRows: 4 }}
              className="drawer-textarea"
              disabled={sendingMessage}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              disabled={!text.trim() || sendingMessage}
              loading={sendingMessage}
              className="drawer-send-btn"
            >
              Send
            </Button>
          </div>
        </div>
      </div>
    </Drawer>
  );
}
// import React, { useState, useRef, useEffect } from "react";
// import {
//   Drawer,
//   Input,
//   Button,
//   Avatar,
//   Empty,
//   Tag,
//   Tooltip,
//   Spin,
//   Popconfirm,
//   Select,
// } from "antd";
// import {
//   SendOutlined,
//   UserOutlined,
//   GlobalOutlined,
//   DollarOutlined,
//   ClockCircleOutlined,
//   DeleteOutlined,
//   SwapOutlined,
// } from "@ant-design/icons";

// const { TextArea } = Input;
// const { Option } = Select;

// export default function ConversationDrawer({
//   card,
//   currentUser,
//   messages,
//   messagesLoading,
//   sendingMessage,
//   onClose,
//   onSend,
//   onDeleteMessage,
//   allColumns,
//   onMoveColumn,
// }) {
//   console.log("messages", messages);
//   const [text, setText] = useState("");
//   const messagesEndRef = useRef(null);

//   // Auto-scroll to bottom when messages change
//   useEffect(() => {
//     if (!messagesLoading) {
//       setTimeout(() => {
//         messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
//       }, 80);
//     }
//   }, [messages.length, messagesLoading]);

//   // Clear input when drawer changes card
//   useEffect(() => {
//     setText("");
//   }, [card?._id]);

//   const handleSend = () => {
//     const trimmed = text.trim();
//     if (!trimmed || sendingMessage) return;
//     onSend(trimmed);
//     setText("");
//   };

//   const handleKeyDown = (e) => {
//     if (e.key === "Enter" && !e.shiftKey) {
//       e.preventDefault();
//       handleSend();
//     }
//   };

//   if (!card) return null;

//   const createdBy = card.createdBy?.name || "—";
//   const assignedTo = card.assignedTo?.name || "—";

//   const otherColumns =
//     allColumns?.filter((col) => col.key !== card.column) || [];

//   return (
//     <Drawer
//       open={!!card}
//       onClose={onClose}
//       width={440}
//       title={null}
//       closable={false}
//       bodyStyle={{
//         padding: 0,
//         display: "flex",
//         flexDirection: "column",
//         height: "100%",
//       }}
//     >
//       {/* ── Header ────────────────────────────────── */}
//       <div className="drawer-header">
//         <div className="drawer-header-top">
//           <div style={{ flex: 1 }}>
//             <h3 className="drawer-job-title">{card.jobName}</h3>
//             <div className="drawer-meta-row">
//               <span className="drawer-meta-item">
//                 <GlobalOutlined style={{ marginRight: 4 }} />
//                 {card.region}
//               </span>
//               <span className="drawer-meta-divider">·</span>
//               <span className="drawer-meta-item">
//                 <DollarOutlined style={{ marginRight: 4 }} />$
//                 {Number(card.projectValue).toLocaleString()}
//               </span>
//               <span className="drawer-meta-divider">·</span>
//               <span className="drawer-meta-item">
//                 <ClockCircleOutlined style={{ marginRight: 4 }} />
//                 {card.duration}
//               </span>
//             </div>
//           </div>
//           <button className="drawer-close-btn" onClick={onClose}>
//             ✕
//           </button>
//         </div>

//         {/* People tags */}
//         <div className="drawer-tags-row" style={{ marginBottom: 10 }}>
//           <Tooltip title="Created by">
//             <Tag icon={<UserOutlined />} className="drawer-tag-created">
//               {createdBy}
//             </Tag>
//           </Tooltip>
//           <Tooltip title="Assigned to">
//             <Tag icon={<UserOutlined />} className="drawer-tag-assigned">
//               {assignedTo}
//             </Tag>
//           </Tooltip>
//         </div>

//         {/* Move column selector */}
//         {otherColumns.length > 0 && (
//           <div className="drawer-move-row">
//             <SwapOutlined
//               style={{ color: "rgba(255,255,255,0.6)", marginRight: 6 }}
//             />
//             <span
//               style={{
//                 color: "rgba(255,255,255,0.7)",
//                 fontSize: 12,
//                 marginRight: 8,
//               }}
//             >
//               Move to:
//             </span>
//             {otherColumns.map((col) => (
//               <button
//                 key={col.key}
//                 className="drawer-move-btn"
//                 style={{ borderColor: col.color, color: col.color }}
//                 onClick={() => onMoveColumn(card._id, card.column, col.key)}
//               >
//                 {col.label}
//               </button>
//             ))}
//           </div>
//         )}
//       </div>

//       {/* ── Section label ─────────────────────────── */}
//       <div className="drawer-section-label">
//         Conversation {messages.length > 0 && `· ${messages.length} messages`}
//       </div>

//       {/* ── Messages ──────────────────────────────── */}
//       <div className="drawer-messages">
//         {messagesLoading ? (
//           <div
//             style={{
//               display: "flex",
//               justifyContent: "center",
//               paddingTop: 60,
//             }}
//           >
//             <Spin tip="Loading conversation..." />
//           </div>
//         ) : messages.length === 0 ? (
//           <Empty
//             image={Empty.PRESENTED_IMAGE_SIMPLE}
//             description="No messages yet. Start the conversation!"
//             style={{ marginTop: 60 }}
//           />
//         ) : (
//           messages.map((msg) => {
//             const isMe = msg?.author?._id === currentUser?._id;
//             const authorName = msg.author?.name || "Unknown";
//             const timeStr = msg.createdAt
//               ? new Date(msg.createdAt).toLocaleTimeString([], {
//                   hour: "2-digit",
//                   minute: "2-digit",
//                 })
//               : "";

//             return (
//               <div
//                 key={msg._id}
//                 className={`drawer-msg-row ${isMe ? "drawer-msg-row--me" : "drawer-msg-row--other"}`}
//               >
//                 {!isMe && (
//                   <Avatar
//                     size={32}
//                     style={{ background: "#13487D", flexShrink: 0 }}
//                   >
//                     {authorName.charAt(0)}
//                   </Avatar>
//                 )}

//                 <div
//                   className={`drawer-msg-bubble ${isMe ? "drawer-msg-bubble--me" : "drawer-msg-bubble--other"}`}
//                 >
//                   {!isMe && (
//                     <span className="drawer-msg-author">{authorName}</span>
//                   )}
//                   <p className="drawer-msg-text">{msg.text}</p>
//                   <div
//                     style={{
//                       display: "flex",
//                       // justifyContent: "space-between",
//                       gap: "5px",
//                       alignItems: "center",
//                     }}
//                   >
//                     <span className="drawer-msg-time">
//                       {msg.isOptimistic ? "Sending…" : timeStr}
//                     </span>
//                     {isMe && !msg.isOptimistic && (
//                       <Popconfirm
//                         title="Delete this message?"
//                         okText="Delete"
//                         cancelText="Cancel"
//                         okButtonProps={{ danger: true }}
//                         onConfirm={() => onDeleteMessage(msg._id)}
//                       >
//                         <DeleteOutlined className="drawer-msg-delete" />
//                       </Popconfirm>
//                     )}
//                   </div>
//                 </div>

//                 {isMe && (
//                   <Avatar
//                     size={32}
//                     style={{ background: "#3699FF", flexShrink: 0 }}
//                   >
//                     {currentUser?.name?.charAt(0) || "U"}
//                   </Avatar>
//                 )}
//               </div>
//             );
//           })
//         )}
//         <div ref={messagesEndRef} />
//       </div>

//       {/* ── Input ─────────────────────────────────── */}
//       <div className="drawer-input-area">
//         <TextArea
//           value={text}
//           onChange={(e) => setText(e.target.value)}
//           onKeyDown={handleKeyDown}
//           placeholder="Type a message… (Enter to send)"
//           autoSize={{ minRows: 2, maxRows: 4 }}
//           className="drawer-textarea"
//           disabled={sendingMessage}
//         />
//         <Button
//           type="primary"
//           icon={<SendOutlined />}
//           onClick={handleSend}
//           disabled={!text.trim() || sendingMessage}
//           loading={sendingMessage}
//           className="drawer-send-btn"
//         >
//           Send
//         </Button>
//       </div>
//     </Drawer>
//   );
// }
